import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import { businessInfo, projectTypes } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact/submit`, formData);
      
      if (response.data) {
        setIsSubmitted(true);
        toast.success('Thank you for your enquiry!', {
          description: "We'll contact you within 24 hours"
        });
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            phone: '',
            email: '',
            projectType: '',
            message: ''
          });
          setIsSubmitted(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Unable to send enquiry', {
        description: `Please call us directly at ${businessInfo.phone}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-[#1c2536]/10 rounded-full mb-4 sm:mb-6">
            <span className="text-[#1c2536] font-semibold text-xs sm:text-sm">CONTACT US</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Book Your Free Design Visit Today
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            Get in touch to discuss your project and receive a no-obligation quote
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12">
          {/* Contact Information */}
          <div className="space-y-4 sm:space-y-6 md:space-y-8">
            {/* Contact Cards */}
            <Card className="border-gray-200 hover:border-[#1c2536]/30 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-4 sm:p-6 md:p-8">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md" style={{ backgroundColor: '#1c2536' }}>
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Call Us</h3>
                    <a
                      href={`tel:${businessInfo.phone}`}
                      className="text-[#1c2536] hover:text-[#1c2536]/80 font-semibold text-base sm:text-lg"
                    >
                      {businessInfo.phone}
                    </a>
                    <p className="text-gray-600 text-xs sm:text-sm mt-1">{businessInfo.hours}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 hover:border-[#1c2536]/30 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-4 sm:p-6 md:p-8">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md" style={{ backgroundColor: '#1c2536' }}>
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Email Us</h3>
                    <a
                      href={`mailto:${businessInfo.email}`}
                      className="text-[#1c2536] hover:text-[#1c2536]/80 font-semibold text-sm sm:text-base break-all"
                    >
                      {businessInfo.email}
                    </a>
                    <p className="text-gray-600 text-xs sm:text-sm mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 hover:border-[#1c2536]/30 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-4 sm:p-6 md:p-8">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md" style={{ backgroundColor: '#1c2536' }}>
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Our Location</h3>
                    <p className="text-gray-700 text-sm sm:text-base">
                      {businessInfo.address.street}<br />
                      {businessInfo.address.area}<br />
                      {businessInfo.address.city} {businessInfo.address.postcode}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Interactive Map */}
            <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border border-gray-200 h-48 sm:h-56 md:h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1178.5!2d-0.5335202!3d53.7192918!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4878e9cabe141fb1%3A0x3f877d766e4af362!2sJP%20Joinery!5e0!3m2!1sen!2suk!4v1709312000000!5m2!1sen!2suk"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="JP Joinery Location"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="border-gray-200 shadow-lg sm:shadow-xl">
            <CardContent className="p-4 sm:p-6 md:p-8">
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-semibold mb-1.5 sm:mb-2 block text-sm sm:text-base">
                      Your Name *
                    </Label>
                    <Input
                      id="name"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                      className="border-gray-300 focus:border-[#1c2536] focus:ring-[#1c2536] text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-semibold mb-1.5 sm:mb-2 block text-sm sm:text-base">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="01482 123456"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                      className="border-gray-300 focus:border-[#1c2536] focus:ring-[#1c2536] text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-semibold mb-1.5 sm:mb-2 block text-sm sm:text-base">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                      className="border-gray-300 focus:border-[#1c2536] focus:ring-[#1c2536] text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <Label htmlFor="projectType" className="text-gray-700 font-semibold mb-1.5 sm:mb-2 block text-sm sm:text-base">
                      Project Type *
                    </Label>
                    <Select
                      value={formData.projectType}
                      onValueChange={(value) => handleChange('projectType', value)}
                      required
                    >
                      <SelectTrigger className="border-gray-300 focus:border-[#1c2536] focus:ring-[#1c2536] text-sm sm:text-base">
                        <SelectValue placeholder="Select project type" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-700 font-semibold mb-1.5 sm:mb-2 block text-sm sm:text-base">
                      Your Message
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us about your project requirements..."
                      rows={3}
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      className="border-gray-300 focus:border-[#1c2536] focus:ring-[#1c2536] text-sm sm:text-base"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-white py-4 sm:py-5 md:py-6 text-sm sm:text-base md:text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                    style={{ backgroundColor: '#1c2536' }}
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-4 h-4 sm:w-5 sm:h-5 mr-2 group-hover:translate-x-1 transition-transform" />
                        Send Enquiry
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                <div className="text-center py-8 sm:py-10 md:py-12">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Thank You!</h3>
                  <p className="text-gray-600 text-base sm:text-lg">
                    Your enquiry has been received. We'll be in touch within 24 hours.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
